import { AddIcon } from '@chakra-ui/icons';
import { HStack } from '@chakra-ui/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import useGetTRSClusters from './queries/useGetTRSClusters';

import { Button, Text } from '@/components/v4';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import useColumnState from '../../../hooks/useColumnState';
import CopyCellRenderer from 'components/tables/AGGrid/CustomCells/CopyCellRenderer';
import ActionsButton from 'components/tables/AGGrid/ActionsButton';
import { Error } from 'components/index';

import { clusterDescSuffix } from './constants';

export default function TRSClusters() {
    const gridRef = useRef();
    const navigate = useNavigate();

    const { currentCompany } = useSelector((state) => state.navigationLists);

    const [tableData, setTableData] = useState({
        columns: [],
        data: [],
    });

    const handleEditCluster = useCallback(
        (clusterData) => {
            navigate(`/trs-clusters/edit/${clusterData.id}`);
        },
        [navigate],
    );

    const handleAddCluster = () => {
        navigate('/trs-clusters/add');
    };

    const baseColumns = useMemo(
        () => [
            {
                headerName: 'Name',
                field: 'name',
                cellRenderer: CopyCellRenderer,
                cellStyle: () => ({ width: '100%' }),
                flex: 1,
                minWidth: 150,
            },
            {
                headerName: 'Region',
                field: 'region?.name',
                cellRenderer: CopyCellRenderer,
                valueGetter: (params) => params.data.region?.name,
                flex: 1,
                minWidth: 120,
            },
            {
                headerName: 'Description',
                field: 'description',
                cellRenderer: CopyCellRenderer,
                valueGetter: (params) => {
                    const regionName = params?.data?.region?.name;
                    const description = `${regionName} ${clusterDescSuffix}`;
                    return description;
                },
                flex: 2,
                minWidth: 200,
            },
            {
                headerName: 'Action',
                maxWidth: 100,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                }),
                lockPosition: 'right',
                cellRenderer: (params) => {
                    return (
                        <ActionsButton
                            actions={[
                                {
                                    label: 'Edit Cluster',
                                    onClick: () =>
                                        handleEditCluster(params.data),
                                },
                            ]}
                        />
                    );
                },
            },
        ],
        [handleEditCluster],
    );

    const { columns, onColumnVisible } = useColumnState(baseColumns);

    const tableDataWithColumns = useMemo(
        () => ({
            columns,
            data: tableData.data,
        }),
        [columns, tableData.data],
    );

    const { refetch, isRefetching, isFetching } = useGetTRSClusters(
        setTableData,
        gridRef,
        baseColumns,
    );

    const titleName = 'TRS Clusters';

    if (!currentCompany) {
        return (
            <Error
                error={{
                    name: 'No Partner / Company / Wholesaler Selected',
                    message:
                        'Please select a wholesaler and a partner / company.',
                }}
            />
        );
    }

    return (
        <>
            <HStack justifyContent="space-between" marginBottom={5}>
                <Text sx={{ fontSize: '20', fontWeight: '500' }}>
                    TRS Clusters
                </Text>
            </HStack>

            <AGGridMain
                gridRef={gridRef}
                tableData={tableDataWithColumns}
                isFetching={isFetching}
                isRefetching={isRefetching}
                titleName={titleName}
                refetch={refetch}
                onColumnVisible={onColumnVisible}
                extraComponents={
                    <HStack pl={5} spacing={2}>
                        <Button
                            leftIcon={<AddIcon />}
                            onClick={handleAddCluster}
                            size="sm"
                            padding="6px 12px">
                            Add Cluster
                        </Button>
                    </HStack>
                }
            />
        </>
    );
}
